import { loadDataset } from "./LoadSampleData.jsx";

const title = "Welcome Tour";
const description = "A quick tour of the web server, submission form, and results page.";


const Tutorial = ({title, setToken}) => (
  <>
    <h3>{title}</h3>
    <h4>Load tutorial dataset</h4>
    <p>
      For this tutorial, we will use one of the provided example datasets that can also be found in the examples table.
      <button 
        className='btn btn-primary btn-sm'
        type='button'
        onClick={() => loadDataset({setToken, datasetId: 1, password: "cell-culture"})}
      >
      <i className="fa-solid fa-file-import"></i> Load cell culture dataset
      </button>
    </p>

    <h4>General overview</h4>
    <div className="row">
      <div className="col-5">
        <p>
          At the top of the website, you find the <mark style={{background:"#d55dfd4d"}}>navigation bar</mark>, which contains links to the publication, documentation, further tutorials, contact information and the GitHub repository which hosts the source code for the mathematical model used by Fatty Acylizer.
          On the left hand side we have the <mark style={{background:"#5ef0ff4d"}}>settings pane</mark>, where we can upload data, adjust settings and download results. 
          The center contains the <mark style={{background:"#dfff5e4d"}}>main content</mark>, which displays the documentation, tutorials and later the analysis results.
        </p>
      </div>
      <div className="col-7">
        <figure>
          <img src="/tutorials/01-welcome-tour/img-01-overview-scheme.svg"
            alt="Screenshot of the webserver with navigation bar, settings pane and main content area annotated" 
            width="100%"
          />
          <figcaption>Screenshot of the web server with navigation bar, settings pane, and main content area annotated.</figcaption>
        </figure>
      </div>
    </div>

    <h4>Settings pane</h4>
    <div className="row">
      <div className="col-5">
        <figure>
          <img src="/tutorials/01-welcome-tour/img-02-settings-pane.svg"
            alt="Screenshot of the settings pane with the different form parts annotated" 
            width="100%"
          />
          <figcaption>Screenshot of the settings pane.</figcaption>
        </figure>
      </div>
      <div className="col-7">
        <p>
          At the <mark style={{background:"#ffb85e33"}}>top of the submission form</mark>, you can either upload new data, or log in to an existing one.
          When uploading a new dataset, you will receive login credentials that allow you to re-access your analysis results or share them with collaborators.
          For this tutorial, we have already loaded a dataset.
        </p>
        <p>
          The <mark style={{background: "#dfff5e33"}}>search bar</mark> below the upload/login tabs allows you to choose assays from the dataset for reconstruction.
          We can remove assays, select new ones, and search for specific assays using the selector.
        </p>
        <p>
          The <mark style={{background: "#5eff7433"}}>lipid class selector</mark> works in the same way.
          It is used to choose for which lipid classes the fatty acyl profile information will be reconstructed.
        </p>
        <p>
          One of the key parameters for the reconstruction is the <mark style={{background: "#5ef0ff33"}}>fatty acid set</mark>, defining what fatty acids are available for the mathematical model. 
          You can either select one of several predefined sets, and/or manually adjust and select fatty acids simply by clicking into the table.
          The y-component denotes the length of the carbon chain, and the x-component the number of double bonds of the fatty acid.
        </p>
        <p>
          <mark style={{background: "#d55dfd33"}}>Additional optimization parameters</mark> can also be adjusted but we will leave them at their default values for this tutorial.
          We will discuss the role of each of these parameters in a separate tutorial.
        </p>
        <p>
          Once you are satisfied with the settings, you can submit the dataset for fatty acyl profile reconstruction by pressing the "submit job" button.
        </p>
      </div>
    </div>


    <h4>Results</h4>
    <p>
      For each of the selected assays in the assay selector, we will receive one result card  in the center.
      <br />
      At the top of each card, there is a small notification that contains the relative lipid profile error.
    </p>
    <div className="grid">
      <div className="row">
        <figure>
          <img src="/tutorials/01-welcome-tour/img-05-results.png"
            alt="Screenshot of a result card."  
            width="100%"
          />
          <figcaption>Screenshot of a result card.</figcaption>
        </figure>
      </div>
      <div className="row">
        <div className="col">
          <h5>Fatty acyl charts</h5>
          <p>
            The first chart contains the reconstructed fatty acyl profile.
            The graph next to is shows the fatty acyl profile over the course of the optimization.
            We can see that the optimization took 23 steps with the abundances of the major fatty acyls changing little.
            This indicates that the initial starting conditions for the optimization were already really close to the optimal solution.

            The two fatty acyl charts are connected, meaning hovering over any bar or line highlights the corresponding line or bar in both charts.
          </p>
        </div>
        <div className="col">
          <h5>Bubble plots</h5>
          <p>
            The plots next to the fatty acyl charts show the experimental and the modeled lipid profiles as bubble plots.
            In a bubble plot each dot represents a lipid species, and the size of a dot represents its abundance. 
            The x-component of a dot is the sum of its carbon chain lengths and the y-component the sum of its double bonds.
            By hovering over one of the lipid species with the mouse, you get a small notification showing the lipid species name and abundance. 
            By clicking on a dot, you can pin and unpin the notification.
          </p>
        </div>
      </div>
    </div>
    <h5>Abundance cutoff</h5>
    <div className="row">
      <div className="col-5">
        <p>
          At the bottom of the results card is a slider, which controls the minimum abundance that is displayed in the charts.
          Any value below this cutoff is not displayed.
          If you set the cut off too low, axis limits may be off due to theoretically possible lipid species with abundances close to 0.
        </p>
      </div>
      <div className="col-7">
        <figure>
          <img src="/tutorials/01-welcome-tour/img-06-slider.png"
            alt="Screenshot showing the effect of the abundance cutoff slider." 
            width="100%"
          />
          <figcaption>The abundance cut off slider set to zero sets the axis limits to include all theoretically possible lipid species.</figcaption>
        </figure>
      </div>
    </div>


    <h5>Detail plots</h5>
    <p>
      If you click on the "Show detail plots" button next to the slider, you can view additional, more in-depth, charts.
    </p>
    <div className="row">
      <figure>
        <img src="/tutorials/01-welcome-tour/img-07-extra-hovered.png"
          alt="Image of a result card with additional in-depth charts at the bottom." 
          width="100%"
        />
        <figcaption>Image of a result card with additional in-depth charts at the bottom.</figcaption>
      </figure>
      <div className="row">
        <div className="col">
          <p>
            The first bubble plot shows the portion of the modeled lipid profile that is influenced by a specified fatty acid.
            By default, the most abundant fatty acid is selected, but you can select any fatty acid with the selector above the chart or by clicking on any fatty acid in the fatty acyl charts.
          </p>
        </div>
        <div className="col">
          <p>
            The second chart shows the theoretically possible molecular lipid species for a lipid species.
            Again,the most abundant lipid species is shown by default, but you can select the lipid species with either the selector or by clicking on any lipid species in any bubble plot.
          </p>
        </div>
        <div className="col">
          <p>
            The last plot shows the difference between the measured and modeled lipid profiles as a bubble plot.
            Dots colored in red indicate an under-representation of this lipid species and blue an over-representation in the modeled profile.
          </p>
        </div>
        <div className="col-2">
        </div>
      </div>
    </div>

    <h4>Download</h4>
    <div className="row">
      <div className="col">
        <p>
          Once you are satisfied with the results, you can return to the settings pane and download them.
          The downloaded ZIP archive contains one folder for each reconstructed assay containing the visualizations and raw data.
          At the root of the archive is the "authentication-information.json" file which contains the login credentials for re-accessing the dataset on the Fatty Acylizer web server.
          The "report.mztab" file contains metadata and a summary table of the results.
          The mztab-M file is text-based, and can be opened with a spreadsheet program such as Excel or LibreOffice.
        </p>
      </div>
      <div className="col">
        <figure>
          <img src="/tutorials/01-welcome-tour/img-08-download.png"
            alt="Image showing the contents of the ZIP archive" 
            width="100%"
          />
          <figcaption>Content of the downloaded ZIP archive.</figcaption>
        </figure>
      </div>
    </div>
    <p>
      With this we are at the end of the welcome-tour of the Fatty Acylizer.
    </p>
  </>
)

export const WelcomeTourTutorial = ({prefix}) => {
  const title_ = prefix ? prefix + title : title;
  return {
    title: title_,
    section: "navigating-ui",
    description: description,
    videoUrl: "https://youtu.be/vnQddmMYAsc",
    tutorial: ({setToken}) => Tutorial({title: title_, setToken})
  }
}
